// Runtime-only session/access guards shared by staff reads and commands.
let epoch = 0
let accessUnavailable = false
export const setStaffAccessUnavailable = (value: boolean) => { accessUnavailable = value }
let recheckAccess: (() => void) | undefined
export function registerStaffAccessRecheck(handler: () => void) {
  recheckAccess = handler
  return () => { if (recheckAccess === handler) recheckAccess = undefined }
}
export function requestStaffAccessRecheck() {
  accessUnavailable = true
  recheckAccess?.()
}
const writeBoundaries = new Set<{ path: string; available: () => boolean }>()
export class StaffWriteUnavailableError extends Error {
  constructor(readonly code: "access-check-unavailable" | "read-reconciliation-unavailable") {
    super(code === "access-check-unavailable"
      ? "Staff access must be verified before saving. The request was not sent."
      : "Saved application state must be refreshed before saving. The request was not sent.")
    this.name = "StaffWriteUnavailableError"
  }
}
/** Temporary legacy writers and RTK mutations share the same pre-send fence. */
export function registerStaffWriteBoundary(path: string, available: () => boolean) {
  const entry = { path, available }
  writeBoundaries.add(entry)
  return () => { writeBoundaries.delete(entry) }
}
export function assertStaffWriteAvailable(path?: string) {
  if (accessUnavailable) throw new StaffWriteUnavailableError("access-check-unavailable")
  if (!path) return
  const pathname = new URL(path, "https://staff.invalid").pathname
  for (const boundary of writeBoundaries) {
    if ((pathname === boundary.path || pathname.startsWith(`${boundary.path}/`)) && !boundary.available()) {
      throw new StaffWriteUnavailableError("read-reconciliation-unavailable")
    }
  }
}
const reads = new Set<AbortController>()
export class StaffSessionExpiredError extends Error {
  constructor() { super("The staff session changed. This response was discarded."); this.name = "StaffSessionExpiredError" }
}
export const captureStaffEpoch = () => epoch
export function assertStaffEpoch(expected: number) { if (expected !== epoch) throw new StaffSessionExpiredError() }
export function advanceStaffEpoch() {
  epoch++
  accessUnavailable = false
  for (const read of reads) read.abort()
  reads.clear()
}
export function observeStaffRead() {
  const controller = new AbortController()
  reads.add(controller)
  return { signal: controller.signal, release: () => { reads.delete(controller) } }
}
